<script>
import ProductFeatures from "@js/components/Sections/Product/ProductFeatures.vue";
import ProductDescription from "@js/components/Sections/Product/ProductDescription.vue";
import ImageSlider from "@js/components/Sections/Gallery/ImageSlider.vue";
import CallbackBigForm from "@js/components/Sections/Callback/CallbackBigForm.vue";
import {useThemeStore} from "@js/store/ThemeStore.js";
import ProductDeliveryInfo from "@js/components/Sections/Product/ProductDeliveryInfo.vue";
import ProductSelfPickupInfo from "@js/components/Sections/Product/ProductSelfPickupInfo.vue";
import {useProductStore} from "@js/store/ProductStore.js";
import ProductBreadcrumbs from "@js/components/Sections/Product/ProductBreadcrumbs.vue";
import CallbackButton from "@js/components/Buttons/CallbackButton.vue";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import ProductAddToCart from "@js/components/Sections/Product/Components/ProductAddToCart.vue";
import IconShare from "@js/components/Icons/IconShare.vue";
import ProductInfo from "@js/components/Sections/Product/ProductInfo.vue";
import IconFactory from "@js/components/Icons/IconFactory.vue";
import IconDeliveryCar from "@js/components/Icons/IconDeliveryCar.vue";

export default {
    name: "ProductPage",
    props: {
        data: {
            required: true,
        }
    },
    components: {
        IconDeliveryCar,
        IconFactory,
        ProductInfo,
        IconShare,
        ProductAddToCart,
        NumberFormatComponent,
        CallbackButton,
        ProductBreadcrumbs,
        ProductSelfPickupInfo,
        ProductDeliveryInfo,
        CallbackBigForm,
        ImageSlider,
        ProductDescription,
        ProductFeatures
    },
    methods: {
        insertSchemaMarkup() {
            if (typeof window !== "undefined") {
                const scriptId = "schema-product-jsonld"
                let script = document.getElementById(scriptId)

                if (script) {
                    script.remove()
                }

                script = document.createElement("script")
                script.id = scriptId
                script.type = "application/ld+json"
                script.textContent = JSON.stringify({
                    "@context": "https://schema.org/",
                    "@type": "Product",
                    "name": this.productStore.product.name ?? "Неизвестный продукт",
                    "image": this.imagesList[0],
                    "description": this.$page.props.description ?? "Описание отсутствует",
                    "sku": this.productStore.product.sku ?? "Не указан",
                    "brand": {
                        "@type": "Brand",
                        "name": "ООО «Дорожные плиты»"
                    },
                    "offers": {
                        "@type": "Offer",
                        "price": this.productStore.product.price ?? "0",
                        "priceCurrency": "RUB",
                        "availability": this.productStore.product.availability
                            ? "https://schema.org/InStock"
                            : "https://schema.org/OutOfStock",
                        "url": window.location.href
                    }
                })

                document.head.appendChild(script);
            }
        },
        removeSchemaMarkup() {
            const script = document.getElementById("schema-product-jsonld")
            if (script) {
                script.remove()
            }
        }
    },
    beforeMount() {
        this.themeStore.activateLightTheme()
    },
    beforeUnmount() {
        this.removeSchemaMarkup()
    },
    mounted() {
        this.productStore.product = this.data.product;
        this.productStore.breadcrumbs = this.data.breadcrumbs;
        this.insertSchemaMarkup()
    },
    computed: {
        imagesList: function () {
            let links = []
            if (this.productStore.product.image !== undefined && this.productStore.product.image !== null && this.productStore.product.image.order.length > 0) {
                this.productStore.product.image.order.forEach(image => {
                    links.push('/storage/products/' + this.productStore.product.image.path + '/' + image)
                })
            } else {
                links.push('/assets/defaults/product-not-found.webp')
            }

            return links
        }
    },
    setup() {
        const themeStore = useThemeStore()
        const productStore = useProductStore()

        themeStore.turnOffMap()

        return {themeStore, productStore}
    }
}
</script>

<template>
    <div v-if="productStore.product">
        <div class="pt-10">
            <div class="container">
                <ProductBreadcrumbs/>
                <div class="flex flex-col md:flex-row mb-5">
                    <h1 class="flex items-center text-2xl md:text-3xl font-bold mb-2 md:mb-0">
                        {{ productStore.product.name }}
                    </h1>
                    <div class="flex items-center">
                        <div :class="[productStore.product.availability ? 'bg-emerald-700' : 'bg-red-600' ]"
                             class="ms-0 md:ms-5 font-bold text-[12px] text-white px-3 py-1 rounded leading-5">
                            {{ productStore.product.availability ? 'В наличии' : 'Нет в наличии' }}
                        </div>
                        <div v-if="productStore.product.bestseller"
                             class="ms-2 font-bold text-[12px] text-white px-3 py-1 rounded bg-[#c52314] leading-5">
                            ХИТ Продаж
                        </div>
                    </div>
                </div>
                <div class="grid grid-cols-12 gap-y-10 md:gap-x-10 text-gray-900 mb-10">
                    <div class="col-span-12 md:col-span-6">
                        <ImageSlider :images="imagesList"/>
                    </div>
                    <div class="col-span-12 md:col-span-6">
                        <ProductFeatures/>
                        <hr class="mb-5 mt-5"/>
                        <ProductAddToCart class="mb-5"/>
                        <div class="flex gap-3">
                           <div class="flex items-center max-w-[160px] px-3 py-2 border border-dashed border-gray-500 rounded-lg">
                               <div class="flex items-center">
                                   <IconFactory class="h-7 w-7 me-3"/>
                                   <div class="text-xs font-semibold leading-tight">
                                       Производство:<br/> от 1 дня
                                   </div>
                               </div>
                           </div>
                            <div class="flex items-center w-full max-w-[200px] px-3 py-2 border border-dashed border-gray-500 rounded-lg">
                                <div class="flex items-center">
                                    <IconDeliveryCar class="h-7 w-7 me-3"/>
                                    <div class="text-xs font-semibold leading-tight">
                                        Доставка по МСК:<br/> от 1 дня
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="grid grid-cols-12 gap-y-10 md:gap-x-10 text-gray-900">
                    <div class="col-span-12 md:col-span-8">
                        <ProductDescription/>
                    </div>
                    <div class="col-span-12 md:col-span-4">
                        <div class="grid grid-cols-12 gap-y-5 md:gap-x-10 text-gray-900">
                            <div class="col-span-12">
                                <ProductDeliveryInfo/>
                            </div>
                            <div class="col-span-12">
                                <ProductSelfPickupInfo/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <CallbackBigForm/>
</template>

<style lang="scss">
body {
    background: #f2f3f6;
}
</style>
